import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Smartphone, Apple, Bot, Share2, Plus,
  CheckCircle2, Zap, WifiOff, Bell,
} from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { HansaLogo, PoweredByBadge } from "../components/HansaLogo";

// Store URLs come from env vars so the user can update them once apps are
// published without touching code. Empty = "Coming soon".
const PLAY_STORE_URL = process.env.REACT_APP_PLAY_STORE_URL || "";
const APP_STORE_URL = process.env.REACT_APP_APP_STORE_URL || "";
const ANDROID_APK_URL = process.env.REACT_APP_ANDROID_APK_URL || "";

export default function DownloadPage() {
  const { t } = useLang();

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1C1C1C]">
      <header className="max-w-5xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
        <Link to="/" data-testid="dl-back" className="flex items-center gap-1 text-[#1B4332] font-semibold hover:underline">
          <ArrowLeft className="w-4 h-4" /> {t("common.back")}
        </Link>
        <div className="flex items-center gap-3">
          <HansaLogo size={40} />
          <LanguageSwitcher compact />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 pb-16">
        {/* Hero */}
        <section className="text-center cd-fade-up py-6 sm:py-12">
          <span className="inline-flex items-center gap-2 bg-[#F4F1EA] text-[#1B4332] border border-[#E5E0D8] px-3 py-1.5 rounded-full text-xs font-semibold">
            <Smartphone className="w-3.5 h-3.5" /> {t("download.tagline")}
          </span>
          <h1 className="cd-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-5 leading-[1.05]">
            {t("download.h1")}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[#2D3A35] max-w-xl mx-auto leading-relaxed">
            {t("download.subtitle")}
          </p>
        </section>

        {/* Store badges */}
        <section className="grid sm:grid-cols-2 gap-4 mt-2">
          <StoreCard
            data-testid="dl-play-store"
            href={PLAY_STORE_URL}
            comingSoon={!PLAY_STORE_URL}
            icon={<Bot className="w-7 h-7 text-white" />}
            label={t("download.playStore.label")}
            primary={t("download.playStore.primary")}
            sub={t("download.playStore.sub")}
            testid="dl-play-store-btn"
          />
          <StoreCard
            data-testid="dl-app-store"
            href={APP_STORE_URL}
            comingSoon={!APP_STORE_URL}
            icon={<Apple className="w-7 h-7 text-white" />}
            label={t("download.appStore.label")}
            primary={t("download.appStore.primary")}
            sub={t("download.appStore.sub")}
            testid="dl-app-store-btn"
          />
        </section>

        {/* APK direct (only if URL set) */}
        {ANDROID_APK_URL && (
          <a
            href={ANDROID_APK_URL}
            data-testid="dl-apk-direct"
            className="mt-4 flex items-center justify-center gap-2 text-sm bg-[#F4F1EA] hover:bg-[#E5E0D8] text-[#1B4332] py-3 rounded-full font-semibold transition-colors"
          >
            <Zap className="w-4 h-4" /> {t("download.apk")}
          </a>
        )}

        {/* PWA install (always available — works today, no store needed) */}
        <section className="mt-12">
          <div className="text-center max-w-xl mx-auto mb-6">
            <h2 className="cd-heading text-2xl sm:text-3xl font-extrabold">{t("download.pwa.title")}</h2>
            <p className="text-sm text-[#52796F] mt-2">{t("download.pwa.subtitle")}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <InstallCard
              testid="dl-pwa-android"
              icon={<Bot className="w-5 h-5 text-[#1B4332]" />}
              title={t("download.pwa.android.title")}
              steps={[
                t("download.pwa.android.s1"),
                t("download.pwa.android.s2"),
                t("download.pwa.android.s3"),
              ]}
              stepIcon={<Plus className="w-3 h-3" />}
            />
            <InstallCard
              testid="dl-pwa-ios"
              icon={<Apple className="w-5 h-5 text-[#1B4332]" />}
              title={t("download.pwa.ios.title")}
              steps={[
                t("download.pwa.ios.s1"),
                t("download.pwa.ios.s2"),
                t("download.pwa.ios.s3"),
              ]}
              stepIcon={<Share2 className="w-3 h-3" />}
            />
          </div>
        </section>

        {/* Why install */}
        <section className="mt-12">
          <div className="text-center max-w-xl mx-auto mb-6">
            <h2 className="cd-heading text-2xl sm:text-3xl font-extrabold">{t("download.why.title")}</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Why icon={<Zap className="w-5 h-5 text-[#D4A373]" />} title={t("download.why.fast")} body={t("download.why.fastBody")} testid="why-fast" />
            <Why icon={<WifiOff className="w-5 h-5 text-[#52796F]" />} title={t("download.why.offline")} body={t("download.why.offlineBody")} testid="why-offline" />
            <Why icon={<Bell className="w-5 h-5 text-[#1B4332]" />} title={t("download.why.notify")} body={t("download.why.notifyBody")} testid="why-notify" />
          </div>
        </section>

        <footer className="mt-14 pt-6 border-t border-[#E5E0D8] flex flex-col items-center gap-1.5 text-sm text-[#52796F]">
          <PoweredByBadge />
          <div className="text-xs">© {new Date().getFullYear()} Hansa</div>
        </footer>
      </main>
    </div>
  );
}

const StoreCard = ({ href, comingSoon, icon, label, primary, sub, testid }) => {
  const inner = (
    <>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#1B4332] flex items-center justify-center shrink-0">{icon}</div>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-[#52796F] font-semibold">{label}</div>
          <div className="cd-heading font-bold text-lg leading-tight">{primary}</div>
          <div className="text-xs text-[#52796F] truncate">{sub}</div>
        </div>
      </div>
      {comingSoon && (
        <span className="absolute top-3 right-3 text-[10px] uppercase font-bold bg-[#D4A373] text-[#1C1C1C] px-2 py-0.5 rounded-full">
          Coming soon
        </span>
      )}
    </>
  );
  const baseClasses = "relative bg-white rounded-2xl p-5 border border-[#E5E0D8] shadow-sm transition-all";
  if (comingSoon || !href) {
    return <div data-testid={testid} className={`${baseClasses} opacity-70 cursor-not-allowed`}>{inner}</div>;
  }
  return (
    <a
      data-testid={testid}
      href={href}
      target="_blank"
      rel="noreferrer"
      className={`${baseClasses} hover:border-[#1B4332] hover:shadow-md cd-lift block`}
    >
      {inner}
    </a>
  );
};

const InstallCard = ({ icon, title, steps, stepIcon, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl p-5 border border-[#E5E0D8] shadow-sm">
    <div className="flex items-center gap-2 cd-heading font-bold text-base">
      {icon} {title}
    </div>
    <ol className="mt-3 space-y-2.5 text-sm text-[#2D3A35]">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-2.5">
          <span className="shrink-0 w-5 h-5 rounded-full bg-[#1B4332] text-[#D4A373] text-[10px] font-bold flex items-center justify-center mt-0.5">
            {i + 1}
          </span>
          <span className="leading-relaxed">
            {s.includes("{icon}") ? (
              <>
                {s.split("{icon}").map((part, idx) => (
                  <React.Fragment key={idx}>
                    {part}
                    {idx === 0 && (
                      <span className="inline-flex items-center justify-center w-4 h-4 rounded bg-[#F4F1EA] mx-0.5 align-text-bottom">
                        {stepIcon}
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </>
            ) : s}
          </span>
        </li>
      ))}
    </ol>
  </div>
);

const Why = ({ icon, title, body, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl p-4 border border-[#E5E0D8]">
    <div className="flex items-center gap-2">
      {icon}
      <CheckCircle2 className="w-3 h-3 text-[#2B9348]" />
    </div>
    <div className="cd-heading font-bold text-base mt-2">{title}</div>
    <div className="text-xs text-[#52796F] mt-1 leading-relaxed">{body}</div>
  </div>
);
